'use client'

import {
  Archive,
  ArrowLeft,
  Calendar,
  Edit3,
  ExternalLink,
  Globe,
  Linkedin,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Plus,
  Send,
  StickyNote,
  Trash2,
  Twitter,
} from 'lucide-react'
import type { GuestProfile as GuestProfileType, Appearance, Communication, StageId } from '@/types'
import { formatDate, formatRelativeTime } from '@/lib/date'
import { useLabels } from '@/lib/terminology'

export interface GuestProfileProps {
  guest: GuestProfileType
  appearances: Appearance[]
  communications: Communication[]
  notes?: string
  canDelete?: boolean
  deleteReason?: string
  isDeleting?: boolean
  onBack: () => void
  onEditGuest: () => void
  onArchiveGuest: () => void
  onDeleteGuest?: () => void
  onAddNote: () => void
  onSendEmail: () => void
  onAppearanceClick: (appearanceId: string) => void
  onInviteToEpisode?: () => void
}

const stageColors: Record<StageId, string> = {
  invited: 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300',
  responded: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
  prep: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
  recorded: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
  published: 'bg-[#6369D1]/20 text-[#6369D1]',
}

const communicationIcons = {
  email: Mail,
  note: StickyNote,
  call: Phone,
}

export function GuestProfile({
  guest,
  appearances,
  communications,
  notes,
  canDelete = false,
  deleteReason: _deleteReason,
  isDeleting = false,
  onBack,
  onEditGuest,
  onArchiveGuest,
  onDeleteGuest,
  onAddNote,
  onSendEmail,
  onAppearanceClick,
  onInviteToEpisode,
}: GuestProfileProps) {
  const labels = useLabels()
  const isArchived = guest.status === 'archived'

  return (
    <div
      className="min-h-full bg-zinc-50 dark:bg-zinc-950 p-4 md:p-6"
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      {/* Header Section */}
      <div className="mb-6">
        {/* Back link */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {labels.guests}
        </button>

        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Avatar */}
          <div className="relative">
            <img
              src={guest.avatarUrl}
              alt={guest.name}
              className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2 border-white dark:border-zinc-800 shadow-md"
            />
            {isArchived && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-zinc-500 flex items-center justify-center">
                <Archive className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    {guest.name}
                  </h1>
                  {isArchived && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400">
                      Archived
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">
                  {guest.title}
                  {guest.company && (
                    <span className="text-zinc-400 dark:text-zinc-500"> @ {guest.company}</span>
                  )}
                </p>
                {guest.location && (
                  <p className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {guest.location}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {canDelete && onDeleteGuest && (
                  <button
                    onClick={onDeleteGuest}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-rose-600 dark:text-rose-400 bg-white dark:bg-zinc-800 border border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-50"
                    title="Permanently delete this guest"
                    data-testid="delete-guest-button"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                )}
                <button
                  onClick={onArchiveGuest}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-600 dark:text-zinc-300 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  <Archive className="w-4 h-4" />
                  {isArchived ? 'Unarchive' : 'Archive'}
                </button>
                <button
                  onClick={onEditGuest}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[#FF6F00] hover:bg-[#FF6F00]/90 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>

            {/* Tags */}
            {guest.tags && guest.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {guest.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#60E1E0]/20 text-[#0D9488] dark:text-[#60E1E0]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (Profile Info) */}
        <div className="space-y-6">
          {/* Contact Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 md:p-5">
            <h2 className="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-4">
              Contact
            </h2>
            <div className="space-y-3">
              {guest.email && (
                <a
                  href={`mailto:${guest.email}`}
                  className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300 hover:text-[#6369D1] dark:hover:text-[#6369D1] transition-colors group"
                >
                  <Mail className="w-4 h-4 text-zinc-400 dark:text-zinc-500 group-hover:text-[#6369D1]" />
                  <span className="truncate">{guest.email}</span>
                </a>
              )}
              {guest.phone && (
                <a
                  href={`tel:${guest.phone}`}
                  className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300 hover:text-[#6369D1] dark:hover:text-[#6369D1] transition-colors group"
                >
                  <Phone className="w-4 h-4 text-zinc-400 dark:text-zinc-500 group-hover:text-[#6369D1]" />
                  <span>{guest.phone}</span>
                </a>
              )}

              {/* Social Links */}
              {guest.socialLinks && (
                <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                  {guest.socialLinks.twitter && (
                    <a
                      href={guest.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 transition-colors"
                      title="Twitter"
                    >
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {guest.socialLinks.linkedin && (
                    <a
                      href={guest.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 transition-colors"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {guest.socialLinks.website && (
                    <a
                      href={guest.socialLinks.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-[#FF6F00] hover:bg-[#FF6F00]/10 transition-colors"
                      title="Website"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}

              {/* Send Email Button */}
              <button
                onClick={onSendEmail}
                className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#FF6F00] hover:bg-[#FF6F00]/90 transition-colors"
              >
                <Send className="w-4 h-4" />
                Send Email
              </button>
            </div>
          </div>

          {/* Bio Card */}
          {guest.bio && (
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 md:p-5">
              <h2 className="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-4">
                Bio
              </h2>
              <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {guest.bio}
              </p>
            </div>
          )}

          {/* Notes Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                Notes
              </h2>
              <button
                onClick={onAddNote}
                className="flex items-center gap-1.5 text-xs font-medium text-[#6369D1] hover:text-[#6369D1]/80 transition-colors"
              >
                {notes ? (
                  <>
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Add note
                  </>
                )}
              </button>
            </div>
            {notes ? (
              <button
                onClick={onAddNote}
                className="w-full text-left text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap hover:bg-zinc-50 dark:hover:bg-zinc-800/50 -mx-2 px-2 py-1 rounded transition-colors cursor-pointer"
              >
                {notes}
              </button>
            ) : (
              <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">
                No notes yet. Add notes to remember important details about this guest.
              </p>
            )}
          </div>
        </div>

        {/* Right Column (Activity) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Appearances Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                {labels.appearances} ({appearances.length})
              </h2>
              {onInviteToEpisode && (
                <button
                  onClick={onInviteToEpisode}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#FF6F00] hover:text-[#FF6F00]/80 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Invite to Episode
                </button>
              )}
            </div>

            {appearances.length > 0 ? (
              <div className="space-y-2">
                {appearances.map((appearance) => (
                  <button
                    key={appearance.id}
                    onClick={() => onAppearanceClick(appearance.id)}
                    className="w-full flex items-center justify-between gap-4 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-left group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate group-hover:text-[#FF6F00] transition-colors">
                        Ep. {appearance.episodeNumber}: {appearance.episodeTitle}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                          <Calendar className="w-3 h-3" />
                          {formatDate(appearance.recordingDate)}
                        </span>
                        {appearance.airDate && (
                          <span className="text-xs text-zinc-400 dark:text-zinc-500">
                            Airs {formatDate(appearance.airDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${stageColors[appearance.stage]}`}
                      >
                        {appearance.stage}
                      </span>
                      <ExternalLink className="w-4 h-4 text-zinc-400 dark:text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">No {labels.appearances.toLowerCase()} yet</p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  This guest hasn't been scheduled for any episodes
                </p>
              </div>
            )}
          </div>

          {/* Communication History Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 md:p-5">
            <h2 className="text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-4">
              Communication History
            </h2>

            {communications.length > 0 ? (
              <div className="space-y-3">
                {communications.map((comm, index) => {
                  const Icon = communicationIcons[comm.type]
                  const isOutbound = comm.direction === 'outbound'
                  const isInbound = comm.direction === 'inbound'

                  return (
                    <div key={comm.id} className="flex gap-3">
                      {/* Timeline indicator */}
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            comm.type === 'email'
                              ? isOutbound
                                ? 'bg-[#FF6F00]/10 text-[#FF6F00]'
                                : 'bg-[#6369D1]/10 text-[#6369D1]'
                              : comm.type === 'note'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                              : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        {index < communications.length - 1 && (
                          <div className="w-0.5 flex-1 bg-zinc-200 dark:bg-zinc-700 mt-2" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {comm.subject && (
                              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                                {comm.subject}
                              </p>
                            )}
                            <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2 mt-0.5">
                              {comm.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {comm.type === 'email' && (
                              <span
                                className={`text-xs font-medium ${
                                  isInbound ? 'text-[#6369D1]' : 'text-[#FF6F00]'
                                }`}
                              >
                                {isInbound ? 'Received' : 'Sent'}
                              </span>
                            )}
                            <span className="text-xs text-zinc-400 dark:text-zinc-500">
                              {formatRelativeTime(comm.date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">No communications yet</p>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  Emails and notes will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
